import { memo, useEffect, useMemo } from "react"
import type {
  Feature,
  FeatureCollection,
  Geometry,
  GeoJsonProperties,
  Point,
  Polygon,
} from "geojson"
import { GeoJSON, useMap } from "react-leaflet"
import { useAppFilter } from "@/hooks/use-app-filter"
import { useCountyGeojson } from "../../hooks/use-county-geojson"
import { appRoute } from "../routes/app-route"
import { TODO } from "@/types/utility"
import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon"
import area from "@turf/area"
import dissolve from "@turf/dissolve"
import { createBackgroundPatternSvg } from "./geojson-loader.helpers"
import { useLookup } from "./geojson.hooks"
import { Lookup, ReverseLookup } from "./geojson.helpers"

/**
 * Injects SVG patterns or soft backgrounds into the map container for use as fill patterns.
 * Shows patterns if accessibility is enabled, otherwise uses soft backgrounds.
 */
function useLeafletPatterns() {
  const map = useMap()
  const { isAccessibilityLayerFilterEnabled } = useAppFilter()

  useEffect(() => {
    const mapContainer = map.getContainer()
    if (!mapContainer) return

    const existing = mapContainer.querySelector("#geojson-patterns")
    if (existing) {
      existing.remove()
    }

    const svg = createBackgroundPatternSvg(isAccessibilityLayerFilterEnabled)
    mapContainer.appendChild(svg)

    return () => {
      existing?.remove()
    }
  }, [map, isAccessibilityLayerFilterEnabled])
}

/**
 * Type guard for FeatureCollection.
 */
function isFeatureCollection(
  data: unknown
): data is FeatureCollection<Polygon, GeoJsonProperties> {
  if (!data || typeof data !== "object") return false
  const obj = data as Record<string, unknown>
  return obj.type === "FeatureCollection" && Array.isArray(obj.features)
}

function getFilteredLookupFeatures(
  geojson: FeatureCollection<Polygon, GeoJsonProperties> | undefined,
  lookup: Lookup,
): FeatureCollection<Polygon, GeoJsonProperties> | undefined {
  if (
    !geojson ||
    typeof geojson !== "object" ||
    geojson.type !== "FeatureCollection" ||
    !Array.isArray(geojson.features) ||
    !lookup
  )
    return undefined;

  const filteredFeatures = geojson.features.filter(
    (f: TODO) => !!lookup?.[f.properties.id]
  )

  if (filteredFeatures.length === geojson.features.length) return geojson;

  return {
    ...geojson,
    features: filteredFeatures,
  };
}


/**
 * Returns a new GeoJSON FeatureCollection with features filtered out
 * by excluding those with ids "nemt_surrounding_counties" and "nemt_all_michigan_upper".
 *
 * @param geojson - A GeoJSON FeatureCollection to filter.
 * @returns A new filtered GeoJSON FeatureCollection, or undefined if input is undefined.
 */
function getFilteredFeaturesOrOriginal(
  geojson: FeatureCollection<Polygon, GeoJsonProperties> | undefined,
): FeatureCollection<Polygon, GeoJsonProperties> | undefined {
  if (
    !geojson ||
    typeof geojson !== "object" ||
    geojson.type !== "FeatureCollection" ||
    !Array.isArray(geojson.features)
  )
    return undefined;

  const excludeIds = new Set([
    "nemt_surrounding_counties",
    "nemt_all_michigan_upper",
    "nemt_all_michigan_lower",
  ]);

  const filteredFeatures = [];
  for (let i = 0; i < geojson.features.length; i++) {
    const feature = geojson.features[i];
    const id = feature?.properties?.id;
    if (!excludeIds.has(id)) {
      filteredFeatures.push(feature);
    }
  }

  if (filteredFeatures.length === geojson.features.length) return geojson;

  return {
    ...geojson,
    features: filteredFeatures,
  };
}

/**
 * Returns features containing the given point.
 */
function getFeaturesContainingPoint(
  geojson: FeatureCollection<Geometry, GeoJsonProperties> | undefined,
  point: [number, number]
): Feature<Geometry, GeoJsonProperties>[] {
  if (!geojson?.features?.length) return []
  const p: Point = {
    type: "Point",
    coordinates: [point[1], point[0]],
  }
  return geojson.features
    .filter(
      (feature) =>
        !!feature.geometry &&
        feature.geometry.type === "Polygon" &&
        booleanPointInPolygon(p, feature.geometry)
    )
}

/**
 * Returns destination features for given start features.
 */
function getDestinationFeatures(
  geojson: FeatureCollection<Geometry, GeoJsonProperties> | undefined,
  startFeatures: Feature<Geometry, GeoJsonProperties>[],
  lookup: Lookup,
): Feature<Geometry, GeoJsonProperties>[] {
  if (!geojson || !startFeatures.length) return []
  const destinationIds = new Set<string>()
  for (const startFeature of startFeatures) {
    const startId = startFeature.properties?.id
    if (!startId) continue
    const connections = lookup[startId]
    if (!connections) continue
    connections.forEach((c) => destinationIds.add(c.to_id))
  }
  return geojson.features
    .filter((feature) => destinationIds.has(feature.properties?.id))
}

/**
 * Returns origin features for given destination features.
 */
function getOriginFeatures(
  geojson: FeatureCollection<Geometry, GeoJsonProperties> | undefined,
  destFeatures: Feature<Geometry, GeoJsonProperties>[],
  reverseLookup: ReverseLookup,

): Feature<Geometry, GeoJsonProperties>[] {
  if (!geojson || !destFeatures.length) return []
  const originIds = new Set<string>()
  for (const destFeature of destFeatures) {
    const destId = destFeature.properties?.id
    if (!destId) continue
    const connections = reverseLookup[destId]
    if (!connections) continue
    connections.forEach((c) => originIds.add(c.from_id))
  }
  return geojson.features
    .filter((feature) => originIds.has(feature.properties?.id))
}

/**
 * Custom hook to encapsulate all geojson loader logic.
 * Returns the relevant geojson features and loading state.
 */
function useGeoJsonLoader() {
  const { isCountyFilterEnabled } = useAppFilter()
  const { data, isLoading, error } = useCountyGeojson()
  const { from: fromPoint, to: toPoint } = appRoute.useSearch({
    select: (s) => ({ from: s.from, to: s.to }),
  })

  const isReady =
    isCountyFilterEnabled &&
    !error &&
    !isLoading &&
    !!data &&
    isFeatureCollection(data) &&
    (!!fromPoint || !!toPoint)

  return {
    isReady,
    error,
    isLoading,
    data: isFeatureCollection(data) ? data : undefined,
    fromPoint,
    toPoint,
  }
}

type PointParam = { latlng: [number, number]; key: string } | undefined

/**
 * Returns filtered origin and destination features based on input points.
 * Features are sorted by area (descending) before being returned.
 */
function useFilteredFeatures(
  geojson: FeatureCollection<Geometry, GeoJsonProperties> | undefined,
  fromPoint: PointParam,
  toPoint: PointParam
) {
  const { data } = useLookup()

  const fromFeatures = useMemo(
    () => (fromPoint ? getFeaturesContainingPoint(geojson, fromPoint.latlng) : []),
    [geojson, fromPoint]
  )
  const toFeatures = useMemo(
    () => (toPoint ? getFeaturesContainingPoint(geojson, toPoint.latlng) : []),
    [geojson, toPoint]
  )

  return useMemo(() => {
    const { lookup, reverseLookup } = data ?? {}
    if (!lookup || !reverseLookup) {
      return;
    }

    let originFeatures: Feature<Geometry, GeoJsonProperties>[] = []
    let destinationFeatures: Feature<Geometry, GeoJsonProperties>[] = []

    if (fromFeatures.length && toFeatures.length) {
      const validFromFeatures = fromFeatures.filter((fromFeature) => {
        const fromId = fromFeature.properties?.id
        if (!fromId) return false
        return toFeatures.some((toFeature) => {
          const toId = toFeature.properties?.id
          if (!toId) return false
          return (
            lookup[fromId]?.some((c) => c.to_id === toId) ||
            reverseLookup[toId]?.some((c) => c.from_id === fromId)
          )
        })
      })

      const validToFeatures = toFeatures.filter((toFeature) => {
        const toId = toFeature.properties?.id
        if (!toId) return false
        return fromFeatures.some((fromFeature) => {
          const fromId = fromFeature.properties?.id
          if (!fromId) return false
          return (
            lookup[fromId]?.some((c) => c.to_id === toId) ||
            reverseLookup[toId]?.some((c) => c.from_id === fromId)
          )
        })
      })

      if (!validFromFeatures.length || !validToFeatures.length) {
        return { originFeatures: [], destinationFeatures: [] }
      }

      originFeatures = validFromFeatures
      destinationFeatures = validToFeatures
    } else if (fromFeatures.length) {
      originFeatures = []
      destinationFeatures = getDestinationFeatures(geojson, fromFeatures, lookup)
    } else if (toFeatures.length) {
      originFeatures = getOriginFeatures(geojson, toFeatures, reverseLookup)
      destinationFeatures = []
    } else {
      return { originFeatures: [], destinationFeatures: [] }
    }

    // Sort features by area descending
    const sortByAreaDesc = (a: Feature<Geometry, GeoJsonProperties>, b: Feature<Geometry, GeoJsonProperties>) => {
      const areaA = area(a)
      const areaB = area(b)
      return areaB - areaA
    }

    return {
      originFeatures: [...originFeatures].sort(sortByAreaDesc),
      destinationFeatures: [...destinationFeatures].sort(sortByAreaDesc),
    }
  }, [data, fromFeatures, toFeatures, geojson])
}

/**
 * Renders a GeoJSON layer with pattern and tooltip.
 */
const RenderGeoJsonLayer = memo(function RenderGeoJsonLayer({
  features,
  color,
  patternId,
  tooltipOverride,
}: {
  features: Feature<Geometry, GeoJsonProperties>[]
  color: string
  patternId?: string
  tooltipOverride?: string
}) {
  const { data } = useLookup()
  const { isAccessibilityLayerFilterEnabled } = useAppFilter()

  if (!features.length || !data) return null

  const idToRouteLongNameMap = data.idToRouteLongNameMap;
  const featureCollection: FeatureCollection<Geometry, GeoJsonProperties> = {
    type: "FeatureCollection",
    features,
  }
  const key = JSON.stringify(features.map((f) => f.properties?.id))

  const whiteHaloStyle = () => ({
    color: '#ffffff',
    weight: 8,
    fillOpacity: 0,
    opacity: 1,
  })

  const mainStyle = () => ({
    fillColor:
      isAccessibilityLayerFilterEnabled && patternId
        ? `url(#${patternId})`
        : color,
    color,
    fillOpacity: isAccessibilityLayerFilterEnabled ? 0.6 : 0.6,
    opacity: 1,
    weight: 3,
    className: 'county-highlight',
  })

  const bindLayerTooltip = (
    feature: Feature<Geometry, GeoJsonProperties>,
    layer: TODO,
  ) => {
    const id = feature?.properties?.id?.toString() || ''
    const content = tooltipOverride ? tooltipOverride : idToRouteLongNameMap[id] ?? id
    layer.bindTooltip?.(content, {
      direction: 'center',
      className: 'text-[0.70rem] border-0 bg-transparent',
    })
  }

  return (
    <>
      <GeoJSON key={`${key}-white-halo`} data={featureCollection} style={whiteHaloStyle} onEachFeature={bindLayerTooltip} />
      <GeoJSON key={`${key}-main`} data={featureCollection} style={mainStyle} onEachFeature={bindLayerTooltip} />
    </>
  )
})

/**
 * Returns a dissolved (merged) version of the GeoJSON feature collection.
 */
function useDissolveGeojson(
  geojson: FeatureCollection<Polygon, GeoJsonProperties> | undefined
): Feature<Polygon, GeoJsonProperties>[] | undefined {
  return useMemo(() => {
    if (!geojson?.features?.length) return undefined;
    const result = dissolve(geojson);
    if (!result) return undefined;

    return result.features;
  }, [geojson]);
}

/**
 * GeoJsonLoader component. Handles loading, filtering, and rendering geojson layers.
 */
export function GeoJsonLoader() {
  const { isDissolveEnabled } = useAppFilter()
  const { isReady, error, isLoading, data, fromPoint, toPoint } = useGeoJsonLoader()
  const { data: lookupData } = useLookup()
  const lookup = lookupData?.lookup
  useLeafletPatterns()

  const filteredFeatures = getFilteredFeaturesOrOriginal(data)
  const filteredLookupFeatures = !lookup ? filteredFeatures : getFilteredLookupFeatures(data, lookup)
  const dissolvedFeatures = useDissolveGeojson(filteredLookupFeatures)
  const d = useFilteredFeatures(
    filteredFeatures,
    fromPoint,
    toPoint
  )

  if (fromPoint && toPoint) return null

  if (isDissolveEnabled && !fromPoint && !toPoint && dissolvedFeatures && dissolvedFeatures?.length > 0) {
    return (
      <RenderGeoJsonLayer
        features={dissolvedFeatures}
        color="#29275B"
        patternId="origin-diagonal"
        tooltipOverride="All Services"
      />
    )
  }

  const { originFeatures, destinationFeatures } = d ?? {}
  if (!isReady || !originFeatures || !destinationFeatures) {
    if (error) return <>Error...</>
    if (isLoading) return <>Loading...</>
    return null
  }

  if (!originFeatures.length && !destinationFeatures.length) return null

  return (
    <>
      <RenderGeoJsonLayer
        features={originFeatures}
        color="#29275B"
        patternId="origin-diagonal"
      />
      <RenderGeoJsonLayer
        features={destinationFeatures}
        color="#29275B"
        patternId="destination-dots"
      />
    </>
  )
}
